import { CANVAS_UI_OVERRIDES } from "@/canvas/canvasOptions";
import {
  Editor,
  Tldraw,
  type TLShape,
  DefaultToolbar,
  SelectToolbarItem,
  HandToolbarItem,
  type DefaultToolbarProps,
} from "tldraw";
import { CanvasMenuPanel } from "@/canvas/components/CanvasPanels";
import { SPACES_SHAPE_UTILS } from "../constants";
import "@/canvas/canvas.css";
import { TLDRAW_LICENSE_KEY } from "@/constants/tldraw";
import { ET_CANVAS_ASSET_URLS } from "@ever-tutor/et-ui";

function ReadonlyToolbar(props: DefaultToolbarProps) {
  return (
    <DefaultToolbar {...props} orientation="vertical">
      <SelectToolbarItem />
      <HandToolbarItem />
    </DefaultToolbar>
  );
}

const CLOSED_COMPONENTS = {
  MainMenu: null,
  PageMenu: null,
  ActionsMenu: null,
  Minimap: null,
  Toolbar: ReadonlyToolbar,
  SharePanel: null,
  QuickActions: CanvasMenuPanel,
  NavigationPanel: null,
  StylePanel: null,
} as const;

export function toEditorSnapshot(
  raw: Record<string, unknown>,
): Parameters<Editor["loadSnapshot"]>[0] {
  // Sync-room format: { documents: [{state, lastChangedClock}], schema, clock, tombstones }
  // Editor format:    { document: { store: Record<id, TLRecord>, schema }, session? }
  if (Array.isArray(raw.documents)) {
    const store: Record<string, unknown> = {};
    for (const doc of raw.documents as Array<{ state: { id: string } }>) {
      store[doc.state.id] = doc.state;
    }
    return { document: { store, schema: raw.schema } } as Parameters<
      Editor["loadSnapshot"]
    >[0];
  }
  return raw as Parameters<Editor["loadSnapshot"]>[0];
}

export function ClosedCanvas({
  snapshot,
  personalShapes = [],
  camera,
}: {
  snapshot: Record<string, unknown> | null | undefined;
  personalShapes?: TLShape[];
  camera?: ReturnType<Editor["getCamera"]> | null;
}) {
  const handleMount = (editor: Editor) => {
    editor.setCameraOptions({ zoomSteps: [1] });
    editor.updateInstanceState({ isGridMode: true });
    if (snapshot) {
      editor.loadSnapshot(toEditorSnapshot(snapshot));
    }
    if (personalShapes.length > 0) {
      editor.store.mergeRemoteChanges(() => {
        editor.store.put(personalShapes);
      });
    }
    if (camera) {
      editor.setCamera(camera, { animation: { duration: 0 } });
    }
    editor.updateInstanceState({ isReadonly: true });
  };

  return (
    <div className="tldraw-canvas">
      <Tldraw
        overrides={CANVAS_UI_OVERRIDES}
        licenseKey={TLDRAW_LICENSE_KEY}
        assetUrls={ET_CANVAS_ASSET_URLS}
        shapeUtils={SPACES_SHAPE_UTILS}
        onMount={handleMount}
        components={CLOSED_COMPONENTS}
      />
    </div>
  );
}
